// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Art programs to GPU textures (M3-04, M3-12).
//
// Every shipped asset is generated in code, so there is nothing to load: a surface's art is a
// list of drawing ops in the machine definition, and this runs them into a canvas the GPU can
// sample. Zero image files, zero licensing, zero upload-moderation surface — and the whole
// playfield is one texture rather than sixty.
//
// The canvas is 2-D and the art engine is written against a structural `Canvas2D` it satisfies,
// which is why the engine itself has a two-hundred-line test double instead of a browser.

import { CanvasTexture, LinearFilter, LinearMipmapLinearFilter, SRGBColorSpace } from 'three'
import { renderArtSurface, type ArtSurface, type Canvas2D } from '../fx/artProgram'
import { paintPackagedIllustration, paintPackagedPlayfield } from './packagedIllustrations'

export interface ArtTextureResult {
  readonly texture: CanvasTexture
  /** Ops the engine could not draw. Always empty for a validated machine; surfaced, not swallowed. */
  readonly skipped: readonly string[]
}

export interface ArtTextureOptions {
  readonly fallback?: readonly [number, number]
  /**
   * The surface's normalised `y` runs *up* the playfield rather than down the canvas.
   *
   * **A playfield needs this; nothing else does.** A machine's playfield art program addresses
   * the machine the way the machine definition does — PH-001's `+Y`, away from the player — so
   * `laneLetters` at `y = 0.876` and lane rollovers at playfield `y = +0.4000` (0.875 of the way
   * up) are two statements of the same place. Three anchors put it beyond doubt: that one, the
   * ray centre at 0.66 against a star dome 0.683 of the way up, and three neon rings whose `x`
   * matches the three PULSAR bumpers to a hundredth.
   *
   * The art engine handles it, flipping the surface once and un-flipping each glyph, so the
   * texture that comes out is an ordinary top-down picture and uploads with Three's ordinary
   * settings. Getting it wrong does not crash or even look broken: the lanes are simply painted
   * at the player's end and the drain at the far end, which reads as "the art is a bit odd".
   */
  readonly yUp?: boolean
  readonly illustrationTile?: number
  readonly title?: string
  readonly printKind?: 'cabinet' | 'backglass'
  readonly physicalAspect?: number
  readonly printMirror?: boolean
  readonly printFront?: boolean
}

/**
 * Render one art surface to a texture at the resolution the definition asks for.
 *
 * `seed` comes from `art.seed`, so a machine's stochastic ops — its starfield, its streaks —
 * are the same on every device and in every session. A backglass that reshuffled its stars on
 * each load would look alive for one game and broken by the third.
 */
export function renderArtTexture(
  surface: ArtSurface,
  seed: number,
  options: ArtTextureOptions = {},
): ArtTextureResult {
  const fallback = options.fallback ?? ([1024, 1024] as const)
  const [w, h] = surface.resolution ?? fallback
  // Guarded rather than assumed. Without this a headless caller gets a `ReferenceError` from
  // three frames inside the renderer, which reads as a bug in the art program rather than as
  // "there is nothing here to draw on".
  const host = (globalThis as { document?: { createElement: (tag: string) => HTMLCanvasElement } })
    .document
  if (host === undefined) {
    throw new Error('renderArtTexture: no document, so there is no canvas to draw the art on')
  }
  const canvas = host.createElement('canvas')
  canvas.width = Math.max(1, Math.round(w))
  canvas.height = Math.max(1, Math.round(h))

  const ctx = canvas.getContext('2d')
  if (ctx === null) throw new Error('renderArtTexture: the browser refused a 2-D context')

  if (options.illustrationTile !== undefined && options.yUp === true)
    paintPackagedPlayfield(ctx, canvas.width, canvas.height, options.illustrationTile, options.physicalAspect)
  const skipped = renderArtSurface(ctx as unknown as Canvas2D, surface, {
    width: canvas.width,
    height: canvas.height,
    seed,
    yUp: options.yUp ?? false,
    componentSources:options.yUp===true,
  })
  if (options.illustrationTile !== undefined && options.yUp !== true) {
    paintPackagedIllustration(ctx, canvas.width, canvas.height, options.illustrationTile, options.physicalAspect, options.printKind === 'cabinet', options.printMirror, options.printFront)
    if (options.printKind !== 'cabinet')
    renderArtSurface(ctx as unknown as Canvas2D, { program: [{ op: 'text', text: options.title ?? '', at: [.5, .12], size: .085, colour: '#A9E8FF' }] },
      { width: canvas.width, height: canvas.height, seed, yUp: false })
  }

  const texture = new CanvasTexture(canvas)
  // The art is authored in sRGB — it is a picture, not a data buffer — and saying so is what
  // keeps the neon from washing out under ACES tone mapping.
  texture.colorSpace = SRGBColorSpace
  texture.anisotropy = 8
  texture.magFilter = LinearFilter
  texture.minFilter = LinearMipmapLinearFilter
  texture.generateMipmaps = true
  // A playfield is viewed at a glancing angle down its whole length; without mipmaps the far
  // end aliases into a shimmering mess the moment the camera moves.
  texture.needsUpdate = true

  return { texture, skipped }
}
